if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.unmutedMediaIds = new Set();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.viewer) return;

        this.addEventListener("click", this.onSoundToggleClick.bind(this));

        // slideChanged is our single source of truth for mobile swipe/drag and arrow navigation.
        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 50),
        );
        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener(
            "click",
            this.onThumbnailClick.bind(this),
          );
          if (
            this.dataset.desktopLayout?.includes?.("thumbnail") &&
            this.mql.matches
          )
            this.removeListSemantic();
        }

        this.initVideoAutoplayInViewport();

        // On mobile, the initial slide may never emit slideChanged until the user interacts.
        window.setTimeout(() => this.autoplayInitialMobileSlide(), 0);

        // Extra safety: when arrow buttons are clicked, ensure playback syncs after scroll settles.
        this.bindMobileViewerButtons();
      }

      disconnectedCallback() {
        this.videoAutoplayObserver?.disconnect?.();
      }

      bindMobileViewerButtons() {
        if (this.mql.matches) return;
        const prev = this.elements.viewer.querySelector(
          'button[name="previous"]',
        );
        const next = this.elements.viewer.querySelector('button[name="next"]');
        [prev, next].filter(Boolean).forEach((btn) => {
          btn.addEventListener("click", () => {
            window.setTimeout(() => this.syncPlaybackToCurrentSlide(), 0);
          });
        });
      }

      getDeferredMediaNodeName(slide) {
        const deferredMedia = slide.querySelector("deferred-media");
        if (!deferredMedia) return null;

        const tpl = deferredMedia.querySelector("template");
        const first = tpl?.content?.firstElementChild;
        return first?.nodeName || null;
      }

      isVideoSlide(slide) {
        // Prefer template inspection (deferred-media not yet loaded), but fall back to DOM/classes.
        const nodeName = this.getDeferredMediaNodeName(slide);
        if (nodeName === "VIDEO" || nodeName === "IFRAME") return true;

        if (
          slide.querySelector(".media-type-video, .media-type-external_video")
        )
          return true;
        if (slide.querySelector("deferred-media[data-media-id]")) return true;
        return false;
      }

      getCurrentSlideFromViewer() {
        // slider-component (viewer) maintains currentPage + sliderItemsToShow in global.js
        const viewer = this.elements.viewer;
        const currentPage = viewer?.currentPage;
        const items = viewer?.sliderItemsToShow;
        if (items?.length && currentPage) return items[currentPage - 1];

        // Fallbacks
        return (
          viewer?.querySelector?.('[id^="Slide-"][data-media-id].is-active') ||
          this.getViewerSlides()[0] ||
          null
        );
      }

      syncPlaybackToCurrentSlide() {
        if (this.mql.matches) return;
        const slide = this.getCurrentSlideFromViewer();
        if (!slide) return;

        if (this.isVideoSlide(slide)) {
          this.loadMedia(slide);
          this.playActiveMedia(slide);
          this.currentAutoplayMediaSlide = slide;
        } else if (this.currentAutoplayMediaSlide) {
          this.currentAutoplayMediaSlide = null;
          window.pauseAllMedia();
        }
      }

      autoplayInitialMobileSlide() {
        // Mobile behavior: autoplay based on the currently active slide in the slider.
        this.syncPlaybackToCurrentSlide();
      }

      initVideoAutoplayInViewport() {
        if (!("IntersectionObserver" in window)) return;

        const slides = this.getViewerSlides();
        if (!slides.length) return;

        const handleIntersection = (entries) => {
          for (const entry of entries) {
            const slide = entry.target;

            // Any visibility => preload the real media so we can show first frame
            // instead of the poster thumbnail by the time we reach autoplay threshold.
            if (entry.intersectionRatio > 0) {
              this.loadMedia(slide);
            }

            // >= 75% visible => autoplay
            if (entry.intersectionRatio >= 0.75) {
              this.playActiveMedia(slide);
              this.currentAutoplayMediaSlide = slide;
              continue;
            }

            // 0% visible => stop
            if (entry.intersectionRatio === 0) {
              // Only stop when the *currently autoplayed* video leaves the viewport.
              if (
                this.currentAutoplayMediaSlide &&
                slide === this.currentAutoplayMediaSlide
              ) {
                this.currentAutoplayMediaSlide = null;
                window.pauseAllMedia();
              }
            }
          }
        };

        this.videoAutoplayObserver = new IntersectionObserver(
          handleIntersection,
          { threshold: [0, 0.75] },
        );
        slides.forEach((slide) => {
          if (this.isVideoSlide(slide))
            this.videoAutoplayObserver.observe(slide);
        });
      }

      loadMedia(activeItem) {
        const deferredMedia = activeItem?.classList?.contains("deferred-media")
          ? activeItem
          : activeItem?.querySelector?.(".deferred-media");
        if (!deferredMedia) return;
        if (deferredMedia.hasAttribute("loaded")) return;
        deferredMedia.loadContent(false);
      }

      applyMutedStateToSlide(slide, mediaId, muted) {
        if (!slide) return;
        this.loadMedia(slide);

        // Track preference per mediaId.
        if (mediaId) {
          if (muted) this.unmutedMediaIds.delete(mediaId);
          else this.unmutedMediaIds.add(mediaId);
        }

        // Keep button state in sync (there can be more than one button for same media id in DOM).
        if (mediaId) {
          this.querySelectorAll(
            `.media-sound-toggle[data-media-id="${mediaId}"]`,
          ).forEach((b) => {
            b.classList.toggle("is-muted", muted);
          });
        }

        const deferredMedia = slide?.classList?.contains("deferred-media")
          ? slide
          : slide?.querySelector?.(".deferred-media");
        if (!deferredMedia) return;

        const tryApply = () => {
          const videos = deferredMedia.querySelectorAll("video");
          const iframe = deferredMedia.querySelector("iframe");

          if (videos?.length) {
            videos.forEach((video) => {
              video.muted = muted;
              // Keep attribute in sync for debugging/inspection.
              if (muted) video.setAttribute("muted", "");
              else video.removeAttribute("muted");

              if (!muted) video.volume = 1;
              if (!muted) {
                const playPromise = video.play();
                if (playPromise?.catch) playPromise.catch(() => {});
              }
            });
            return true;
          }

          if (iframe?.contentWindow) {
            // YouTube
            if (iframe.classList.contains("js-youtube")) {
              iframe.contentWindow.postMessage(
                `{"event":"command","func":"${muted ? "mute" : "unMute"}","args":""}`,
                "*",
              );
              return true;
            }

            // Vimeo
            if (iframe.classList.contains("js-vimeo")) {
              iframe.contentWindow.postMessage(
                `{"method":"setVolume","value":${muted ? 0 : 1}}`,
                "*",
              );
              return true;
            }
          }

          return false;
        };

        // deferred-media injects <video>/<iframe> async; retry briefly.
        const start = Date.now();
        const retryForMs = 1500;
        const retryEveryMs = 100;

        const attempt = () => {
          if (tryApply()) return;
          if (Date.now() - start >= retryForMs) return;
          window.setTimeout(attempt, retryEveryMs);
        };

        attempt();
      }

      onSoundToggleClick(event) {
        const btn = event.target?.closest?.(".media-sound-toggle");
        if (!btn || !this.contains(btn)) return;

        event.preventDefault();
        event.stopPropagation();

        const mediaId = btn.dataset.mediaId;
        // The toggle button itself has [data-media-id], so don't use closest([data-media-id])
        // as that would just return the button. We need the actual deferred-media container.
        const deferredMediaHost =
          btn.closest("deferred-media.deferred-media") ||
          btn.closest(".deferred-media") ||
          null;

        const slide =
          deferredMediaHost ||
          btn.closest("[data-media-id]:not(.media-sound-toggle)") ||
          (mediaId
            ? this.querySelector(`[data-media-id$="-${mediaId}"]`)
            : null);

        if (!slide) return;

        // Simple behavior: clicking toggles actual mute state.
        // If media isn't injected yet, fall back to button state.
        const deferredMedia = slide?.classList?.contains("deferred-media")
          ? slide
          : slide?.querySelector?.(".deferred-media");
        const video = deferredMedia?.querySelector?.("video");
        const nextMuted = video
          ? !video.muted
          : !btn.classList.contains("is-muted");

        this.applyMutedStateToSlide(slide, mediaId, nextMuted);
      }

      onThumbnailClick(event) {
        if (event.target.closest('button[name="previous"], button[name="next"]'))
          return;

        const thumbnail = event.target.closest("[data-target]");
        if (!thumbnail || !this.elements.thumbnails.contains(thumbnail)) return;

        const button = thumbnail.querySelector("button");
        if (!button) return;

        const mediaId = thumbnail.dataset.target;
        if (!mediaId) return;

        event.preventDefault();
        event.stopPropagation();
        this.setActiveMedia(mediaId, false);
      }

      onSlideChanged(event) {
        if (this.ignoreSlideChanged) return;

        const currentSlide = event?.detail?.currentElement;
        const mediaId = currentSlide?.dataset?.mediaId;

        // Keep "is-active" in sync on mobile when user swipes/drags.
        // CSS relies on .is-active to show deferred-media for non-image slides.
        if (currentSlide) {
          this.elements.viewer
            .querySelectorAll('[id^="Slide-"][data-media-id].is-active')
            .forEach((el) => {
              if (el !== currentSlide) el.classList.remove("is-active");
            });
          currentSlide.classList.add("is-active");
        }

        // Mobile + slider interactions: drive video playback based on the visible slide.
        this.syncPlaybackToCurrentSlide();

        // Thumbnail sync (desktop layouts only)
        if (!mediaId || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      getViewerSlides() {
        return Array.from(
          this.elements.viewer.querySelectorAll(
            '[id^="Slide-"][data-media-id]',
          ),
        );
      }

      isVisibleThumbnail(thumbnail) {
        if (!thumbnail) return false;
        if (getComputedStyle(thumbnail).display === "none") return false;
        return thumbnail.getBoundingClientRect().width > 0;
      }

      getVisibleThumbnails() {
        return Array.from(
          this.elements.thumbnails?.querySelectorAll("[data-target]") || [],
        ).filter((thumbnail) => this.isVisibleThumbnail(thumbnail));
      }

      resolveSlideForThumbnail(mediaId) {
        const slide = this.elements.viewer.querySelector(
          `[id^="Slide-"][data-media-id="${mediaId}"]`,
        );
        if (slide) return slide;

        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!thumbnail) return null;

        const visibleThumbs = this.getVisibleThumbnails();
        const viewerSlides = this.getViewerSlides();
        const thumbIndex = visibleThumbs.indexOf(thumbnail);

        if (thumbIndex >= 0 && thumbIndex < viewerSlides.length) {
          return viewerSlides[thumbIndex];
        }

        return null;
      }

      viewerShowsAllSlides() {
        const slides = this.getViewerSlides();
        if (slides.length < 2) return false;

        const inactive =
          slides.find((slide) => !slide.classList.contains("is-active")) ||
          slides[1];
        return getComputedStyle(inactive).display !== "none";
      }

      scrollViewerToSlide(slide, slideIndex) {
        const viewer = this.elements.viewer;
        const slider = viewer?.slider;
        if (!slider || !slide) return;

        const slides = this.getViewerSlides();
        const index =
          slideIndex >= 0 ? slideIndex : slides.indexOf(slide);

        // Heidi custom.css keeps every .product__media-item on display:block at 750px+,
        // so the main gallery is a vertical stack — scroll the slide into view.
        if (this.viewerShowsAllSlides()) {
          slide.scrollIntoView({ behavior: "smooth", block: "nearest" });
          if (index >= 0) viewer.currentPage = index + 1;
          return;
        }

        const slideOffset =
          index >= 0 && viewer.sliderItemOffset
            ? index * viewer.sliderItemOffset
            : slide.offsetLeft;

        if (typeof viewer.setSlidePosition === "function") {
          viewer.setSlidePosition(slideOffset);
        } else {
          slider.scrollTo({ left: slideOffset });
        }

        if (index >= 0) viewer.currentPage = index + 1;
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.resolveSlideForThumbnail(mediaId);
        if (!activeMedia) return;

        const viewerSlides = this.getViewerSlides();
        const slideIndex = viewerSlides.indexOf(activeMedia);

        this.ignoreSlideChanged = true;
        viewerSlides.forEach((element) => {
          element.classList.remove("is-active");
        });
        activeMedia.classList.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        // Scroll whenever thumbnails exist (Dawn default) or on mobile.
        const shouldScrollViewer =
          !this.mql.matches || Boolean(this.elements.thumbnails);
        window.setTimeout(() => {
          if (shouldScrollViewer) {
            this.scrollViewerToSlide(activeMedia, slideIndex);
          } else if (slideIndex >= 0) {
            this.elements.viewer.currentPage = slideIndex + 1;
          }

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top <= -0.5) {
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: "smooth" });
          }

          window.setTimeout(() => {
            this.ignoreSlideChanged = false;
          }, 200);
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((element) => element.classList.remove("active"));
        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider;
        const flexDirection = getComputedStyle(slider).flexDirection;
        const isColumn =
          flexDirection === "column" || flexDirection === "column-reverse";

        if (isColumn) {
          const maxScrollTop = Math.max(
            0,
            slider.scrollHeight - slider.clientHeight,
          );
          const targetTop = Math.min(thumbnail.offsetTop, maxScrollTop);
          slider.scrollTo({ top: targetTop });
          return;
        }

        const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const targetLeft = Math.min(thumbnail.offsetLeft, maxScrollLeft);

        if (typeof this.elements.thumbnails.setSlidePosition === "function") {
          this.elements.thumbnails.setSlidePosition(targetLeft);
        } else {
          slider.scrollTo({ left: targetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (!deferredMedia) return;

        if (!deferredMedia.hasAttribute("loaded"))
          deferredMedia.loadContent(false);

        // Play (content may have just been inserted).
        const video = deferredMedia.querySelector("video");
        if (video) {
          const mediaId = deferredMedia.dataset?.mediaId;
          const shouldBeUnmuted = mediaId && this.unmutedMediaIds.has(mediaId);
          video.muted = !shouldBeUnmuted;
          if (!shouldBeUnmuted) video.setAttribute("muted", "");
          else video.removeAttribute("muted");
          if (shouldBeUnmuted) video.volume = 1;
          video.playsInline = true;
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
          if (mediaId) {
            const toggle = this.querySelector(
              `.media-sound-toggle[data-media-id="${mediaId}"]`,
            );
            if (toggle) toggle.classList.toggle("is-muted", !shouldBeUnmuted);
          }
          return;
        }

        const iframe = deferredMedia.querySelector("iframe");
        if (!iframe?.contentWindow) return;

        // YouTube
        if (iframe.classList.contains("js-youtube")) {
          iframe.contentWindow.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            "*",
          );
          return;
        }

        // Vimeo
        if (iframe.classList.contains("js-vimeo")) {
          iframe.contentWindow.postMessage('{"method":"play"}', "*");
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
